/**
 * This component will be responsible for selecting labels grouped by categories
 * Similar to BranchFilterComponent but for labels with category grouping
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import "./style.scss";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { Checkbox } from "@mui/material";
import CtaComponent from "../cta";
import useDebounce from "../../../hooks/useDebounce";
import { cloneDeep } from "lodash";
import CModal from "../Modal";

interface Props {
  branchLabels: string[];
  branchLabelsCategory: string[];
  branchLabelsCategoryMap: Record<string, string>; // { label: category }
  onApplyCtaClick: (selectedLabels: string[]) => void;
  selectAllLabels?: boolean;
  customClass?: string;
  parentClass?: string;
  modalCustomClass?: string;
  alignmentChangeRequired?: boolean;
  preSelectedLabels?: string[];
}

const UNCATEGORIZED = "Uncategorized";

const BranchLabelFilter = (props: Props) => {
  const {
    branchLabels = [],
    branchLabelsCategory = [],
    branchLabelsCategoryMap = {},
    onApplyCtaClick,
    selectAllLabels = false,
    customClass = "",
    parentClass = "",
    modalCustomClass = "",
    alignmentChangeRequired = false,
    preSelectedLabels = []
  } = props;

  const [searchValue, setSearchInput] = useState<string>("");
  const [labelsDataMap, setLabelsDataMap] = useState<any>({});
  const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
  const [activeLabelLength, setActiveLabelLength] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedLabelsObj, setSelectedLabelsObj] = useState<any>({}); // { category: { label: label } }
  const [searchResults, setSearchResults] = useState<any>([]);
  const debouncedSearchTerm: string = useDebounce<string>(searchValue, 500);
  const prevSelection = useRef<any>({});
  const [defaultLabelsSelected, setDefaultLabelsSelected] = useState(false);

  /**
   * Create a map of labels grouped by categories
   * Labels not mapped to any category go under "Uncategorized"
   */
  const createLabelsDataMap = () => {
    const newLabelsData: any = {};
    let activeLabels = 0;

    // Initialize categories
    branchLabelsCategory.forEach((category) => {
      newLabelsData[category] = {};
    });

    // Add uncategorized if there are unmapped labels
    const hasUncategorized = branchLabels.some(
      (label) => !branchLabelsCategoryMap[label]
    );
    if (hasUncategorized) {
      newLabelsData[UNCATEGORIZED] = {};
    }

    // Map labels to their categories
    branchLabels.forEach((label) => {
      activeLabels++;
      const category = branchLabelsCategoryMap[label] || UNCATEGORIZED;
      if (!newLabelsData[category]) {
        newLabelsData[category] = {};
      }
      newLabelsData[category][label] = label;
    });

    setActiveLabelLength(activeLabels);
    return newLabelsData;
  };

  useEffect(() => {
    const data = createLabelsDataMap();
    setLabelsDataMap(data);
    const firstCategory = Object.keys(data)[0];
    if (firstCategory) {
      setSelectedCategory(firstCategory);
    }
  }, [branchLabels, branchLabelsCategory, branchLabelsCategoryMap]);

  useEffect(() => {
    if (preSelectedLabels && preSelectedLabels.length > 0) {
      const selectedMap: any = {};
      preSelectedLabels.forEach((label) => {
        const category = branchLabelsCategoryMap[label] || UNCATEGORIZED;
        if (!selectedMap[category]) {
          selectedMap[category] = {};
        }
        selectedMap[category][label] = label;
      });
      setSelectedLabelsObj(selectedMap);
      prevSelection.current = cloneDeep(selectedMap);
    } else if (preSelectedLabels && preSelectedLabels.length === 0) {
      // Clear selection when preSelectedLabels is explicitly empty
      setSelectedLabelsObj({});
      prevSelection.current = {};
    }
  }, [preSelectedLabels, branchLabelsCategoryMap]);

  useEffect(() => {
    // Only auto-select all labels if selectAllLabels is true AND no preSelectedLabels are provided
    if (selectAllLabels && labelsDataMap && Object.keys(labelsDataMap).length > 0 && (!preSelectedLabels || preSelectedLabels.length === 0)) {
      if (!defaultLabelsSelected) {
        setDefaultLabelsSelected(true);
        const defaultLabelsMap = cloneDeep(labelsDataMap);
        setSelectedLabelsObj(defaultLabelsMap);
        prevSelection.current = cloneDeep(defaultLabelsMap);
        const all_selected_labels: string[] = [];
        for (let category in defaultLabelsMap) {
          all_selected_labels.push(...Object.keys(defaultLabelsMap[category]));
        }
        onApplyCtaClick(all_selected_labels);
      }
    } else if (!selectAllLabels) {
      // Reset defaultLabelsSelected when selectAllLabels is false
      setDefaultLabelsSelected(false);
    }
  }, [labelsDataMap, selectAllLabels, preSelectedLabels]);

  // Whenever the debounce term change it will be triggered
  useEffect(() => {
    if (debouncedSearchTerm) {
      setSearchResults(getSearchResults());
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchTerm]);

  /**
   * This function will return the search results based upon the value entered in search box
   */
  function getSearchResults() {
    const results = branchLabels.filter((label: string) => {
      return label
        ?.toLocaleLowerCase()
        ?.includes(searchValue.toLocaleLowerCase());
    });
    return results;
  }

  /**
   * This function will be triggered when user clicks on label. It will update the selectedLabelsObj
   * @param label : the label name
   * @param isSelected : it identifies whether user has select/deselect a label
   * @param labelCategory: it identifies the category of the label
   */
  const onLabelSelection = (
    label: string,
    isSelected: boolean = false,
    labelCategory: string = ""
  ) => {
    let labelSelectionMap = cloneDeep(selectedLabelsObj);
    if (labelCategory) setSelectedCategory(labelCategory);
    else labelCategory = selectedCategory;

    if (labelSelectionMap[labelCategory]) {
      if (isSelected) delete labelSelectionMap[labelCategory][label];
      else labelSelectionMap[labelCategory][label] = label;
    } else {
      labelSelectionMap[labelCategory] = {};
      labelSelectionMap[labelCategory][label] = label;
    }

    // Remove empty categories
    if (
      labelSelectionMap[labelCategory] &&
      Object.keys(labelSelectionMap[labelCategory]).length === 0
    ) {
      delete labelSelectionMap[labelCategory];
    }

    setSelectedLabelsObj(labelSelectionMap);
  };

  /**
   * This function will be triggered when user clicks on the select all checkbox of a particular category
   * @param category
   * @param isChecked
   */
  const onSelectAllClick = (category: string, isChecked: boolean) => {
    let labelSelectionMap = cloneDeep(selectedLabelsObj);
    if (!isChecked) {
      labelSelectionMap[category] = cloneDeep(labelsDataMap[category]);
    } else {
      delete labelSelectionMap[category];
    }
    setSelectedLabelsObj(labelSelectionMap);
  };

  /**
   * This function will return a boolean value to check if all the labels of a category has been selected or not
   */
  const areAllLabelsOfCategorySelected = (category: string): boolean => {
    return (
      selectedLabelsObj?.[category] &&
      labelsDataMap?.[category] &&
      Object.keys(selectedLabelsObj?.[category])?.length ===
        Object.keys(labelsDataMap?.[category])?.length
        ? true
        : false
    );
  };

  /**
   * This function will be triggered when user clicks on apply button and it will call the parent function and pass all the
   * selected labels in an array
   */
  const onApplyBtnClick = () => {
    const all_selected_labels: string[] = [];
    for (let category in selectedLabelsObj) {
      all_selected_labels.push(...Object.keys(selectedLabelsObj[category]));
    }
    onApplyCtaClick(all_selected_labels);
    prevSelection.current = cloneDeep(selectedLabelsObj);
    setIsShowFilterModal(false);
    setSearchInput("");
  };

  const resetData = () => {
    setSelectedLabelsObj({});
    onApplyCtaClick([]);
    prevSelection.current = {};
    setSearchInput("");
  };

  const getTotalSelectionCount = (
    selectedLabelsData: any = selectedLabelsObj
  ) => {
    let count = 0;
    for (let category in selectedLabelsData) {
      count += Object.keys(selectedLabelsData[category]).length || 0;
    }
    return count;
  };

  function renderActionBox(): React.JSX.Element {
    return (
      <section className="px-16 py-12 flex justify-between items-center absolute bottom-0 bg-white w-full border-t-[1px] border-[#EFEFEF]">
        <div>
          <p className="text-xs font-normal text-[#969696] mb-8">
            Total Labels Selected
          </p>
          <p className="text-base text-black text-[0.935rem] opacity-80 tracking-wider">
            <span className="font-semibold">{getTotalSelectionCount()}</span>/
            {activeLabelLength}
          </p>
        </div>
        <CtaComponent
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.secondary_active_cta}
          onCtaClick={onApplyBtnClick}
          ctaText={"Confirm"}
          isDisabled={false}
        ></CtaComponent>
      </section>
    );
  }

  function renderLabelsByCategory(): React.JSX.Element {
    if (!selectedCategory || !labelsDataMap[selectedCategory]) {
      return <div className="p-16 text-14 opacity-50">No labels in this category</div>;
    }

    return (
      <section className={`w-full max-h-[100%] overflow-auto px-[16px]`}>
        {Object.keys(labelsDataMap[selectedCategory])?.map((label: string) => {
          const isSelected = selectedLabelsObj[selectedCategory]?.[label]
            ? true
            : false;
          return (
            <div
              key={label}
              className={`flex items-center my-20 cursor-pointer text-14 sm:text-[14px]
                         ${isSelected ? "font-semibold" : "font-regular"}`}
              onClick={() => {
                onLabelSelection(
                  label,
                  isSelected ? true : false,
                  selectedCategory
                );
              }}
            >
              <Checkbox
                size={"small"}
                checked={isSelected}
                inputProps={{ "aria-label": "controlled" }}
                className="!p-0"
              />
              <div className="ml-8 font-normal">{label}</div>
            </div>
          );
        })}
      </section>
    );
  }

  function renderCategories(): React.JSX.Element {
    return (
      <section className="border-r-[1px] border-[#EFEFEF] max-h-[100%] overflow-auto shrink-0">
        {Object.keys(labelsDataMap).map((category: string) => {
          const isSelected = selectedCategory === category;
          const isAllSelected = areAllLabelsOfCategorySelected(category);
          const selectedCount = Object.keys(selectedLabelsObj?.[category] ?? {}).length;
          const totalCount = Object.keys(labelsDataMap?.[category] ?? {}).length;

          return (
            <div
              key={category}
              className={`w-full cursor-pointer py-16 flex flex-row justify-between items-center year-box px-12 border-l-[4px] hover:bg-[#F8F8F8] break-words 
              ${
                isSelected
                  ? "bg-[#f2f8ff] border-[#0074FB]"
                  : "bg-white border-transparent"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              <div className="flex items-center mr-12">
                <Checkbox
                  size={"small"}
                  onChange={(e, checked) => onSelectAllClick(category, !checked)}
                  checked={isAllSelected}
                  inputProps={{ "aria-label": "controlled" }}
                  className="!p-0"
                />
                <p
                  className={`text-14 text-sm ${isSelected ? "font-semibold" : ""} ml-8 sm:text-[14px]`}
                >
                  {category}
                </p>
              </div>
              <span
                style={{ borderRadius: 8 }}
                className="px-[6px] py-2 text-xs text-[#767676] bg-[#f4f4f4]"
              >
                <span className="font-semibold text-[#636363]">
                  {selectedCount}
                </span>
                /{totalCount}
              </span>
            </div>
          );
        })}
      </section>
    );
  }

  /**
   * This function will render the search results
   */
  function renderSearchResults(): React.JSX.Element {
    return (
      <section
        className={`flex flex-col max-h-[100%] px-16 pt-70 pb-70`}
      >
        {searchResults.length ? (
          <>
            <p className="text-14 font-normal opacity-50">
              {searchResults.length} Result found
            </p>
            <div className="overflow-auto">
              {searchResults.map((label: string) => {
                const category = branchLabelsCategoryMap[label] || UNCATEGORIZED;
                const isSelected = !!selectedLabelsObj[category]?.[label];
                return (
                  <div
                    key={label}
                    className={`flex items-center my-20 cursor-pointer text-14
                          ${isSelected ? "font-semibold" : "font-regular"}`}
                    onClick={() => onLabelSelection(label, isSelected, category)}
                  >
                    <Checkbox
                      size={"small"}
                      checked={isSelected}
                      inputProps={{ "aria-label": "controlled" }}
                      className="!p-0"
                    />
                    <div className="ml-8">{label}</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-14 font-normal opacity-50">No results found</p>
        )}
      </section>
    );
  }

  const isAllLabelsSelected = (): boolean => {
    return getTotalSelectionCount() === activeLabelLength;
  };

  function renderAllLabelsSelection(): React.JSX.Element {
    const isSelected = isAllLabelsSelected();
    return (
      <div
        onClick={() => {
          setSelectedLabelsObj(isSelected ? {} : cloneDeep(labelsDataMap));
        }}
        className={`flex items-center pt-12 px-16 mt-16 border-[#EFEFEF] border-t-[1px] select-all-label-option text-14 
         justify-between font-medium`}
      >
        <div className="flex items-center cursor-pointer w-full select-none">
          <Checkbox
            size={"small"}
            checked={isSelected}
            inputProps={{ "aria-label": "controlled" }}
            className="!p-0"
          />
          <p className="font-medium text-[0.938rem] ml-8">Select All Labels</p>
        </div>
      </div>
    );
  }

  function toggleModal() {
    // disabled if no data present
    if (Object.keys(labelsDataMap || {}).length) {
      setIsShowFilterModal(true);
    }
  }

  const closeFilterModal = () => {
    setIsShowFilterModal(false);
    setSelectedLabelsObj(prevSelection?.current);
    setIsShowFilterModal(false);
    setSearchInput("");
  }

  return (
    <>
      <div
        className={`branch-label-filter-component relative w-[190px] h-[36px] ${parentClass || ""
          } sm:w-[100%] ml-16 sm:ml-[0px] sm:mt-12`}
        onClick={toggleModal}
      >
        <div
          className={`placeholder-box cursor-pointer whitespace-nowrap w-full flex items-center h-[36px] hover:border-black border-[#D8D8D8] rounded-[5px] border-[1px] pl-16 pr-30 text-15 ${customClass || ""
            } ${!getTotalSelectionCount() && "text-[#A1A2A3]"}`}
        >
          <p className="max-w-full text-ellipsis overflow-hidden">
            {getTotalSelectionCount()
              ? `${getTotalSelectionCount()} Label${getTotalSelectionCount() > 1 ? "s" : ""} Selected`
              : "Labels"}
          </p>
          <div
            className={`absolute right-[12px] top-[calc(50%-8px)] rista-sprite ${isShowFilterModal
                ? "ic-arrow-up-grey-sm"
                : "ic-arrow-down-grey-sm"
              }`}
          ></div>
        </div>
      </div>
      <CModal
        isOpen={isShowFilterModal}
        handleClose={closeFilterModal}
        labelTitle="branch-label-filter-modal"
        describeTitle="branch-label-filter-modal-description"
        body={        
        <div
          className={`modalCenter border-[#F0F0F0] border-[1px] w-[400px] sm:w-[100%] shadow-[0px_2px_10px_#00000014] p-[0px] md:h-[60vh] md:max-h-[440px] sm:h-[440px] pt-8 z-2000 bg-white rounded-[8px] ${modalCustomClass} ${alignmentChangeRequired ? "alignment-class" : ""
            }`}
        >
          <div className="search-label-wrap absolute top-0 bg-white w-full p-16 border-[#EFEFEF] border-b-[1px] pb-12">
            <input
              type="text"
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-input-box text-13 bg-[#FAFAFA] font-regular pr-16 pl-[40px] focus:pl-16 py-8 rounded-[6px] border-[1px] border-[#EFEFEF] w-[100%] focus-visible:border-[#000000] sm:text-[14px]"
              placeholder="Search by label name"
              value={searchValue}
            ></input>
            <div className="rista-sprite ic-search absolute top-[22px] left-[26px] scale-[0.83]"></div>
            {searchValue.length < 3 && renderAllLabelsSelection()}
          </div>
          {searchValue.length >= 3 ? (
            renderSearchResults()
          ) : (
            <section
              className={`flex max-h-[100%] h-[100%] pt-[105px] pb-70`}
            >
              {renderCategories()}
              {renderLabelsByCategory()}
            </section>
          )}
          {renderActionBox()}
        </div>
        }
      >
      </CModal>
    </>
  );
};

export default BranchLabelFilter;
